import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  BarChart3,
  RefreshCw,
  Search,
  Save,
  X,
  Heart,
  X as CloseIcon,
} from "lucide-react";
import { API, authApi, authHeaders, formatError } from "../../lib/adminApi";
import { Logo } from "../../components/Logo";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pain-points"); // pain-points | analytics
  const [painPoints, setPainPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // pain point being edited, or "new"
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (!authApi.isAuthenticated()) {
      navigate("/admin");
      return;
    }
    loadPainPoints();
  }, [navigate]);

  const loadPainPoints = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pain-points?only_active=false`);
      setPainPoints(data);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/swipe-analytics`, {
        headers: authHeaders(),
      });
      setAnalytics(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        authApi.logout();
        navigate("/admin");
      }
      setError(formatError(e));
    }
  };

  useEffect(() => {
    if (tab === "analytics") loadAnalytics();
  }, [tab]);

  const handleSave = async (payload, id) => {
    setBusy(true);
    setError(null);
    try {
      if (id) {
        await axios.put(`${API}/admin/pain-points/${id}`, payload, {
          headers: authHeaders(),
        });
      } else {
        await axios.post(`${API}/admin/pain-points`, payload, {
          headers: authHeaders(),
        });
      }
      setEditing(null);
      await loadPainPoints();
    } catch (e) {
      if (e?.response?.status === 401) {
        authApi.logout();
        navigate("/admin");
        return;
      }
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this pain point? This can't be undone.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/pain-points/${id}`, {
        headers: authHeaders(),
      });
      await loadPainPoints();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleReseed = async () => {
    if (
      !window.confirm(
        "Reseed will DELETE all pain points and reload the original 100 from the seed file. Continue?",
      )
    )
      return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/pain-points/reseed`,
        {},
        { headers: authHeaders() },
      );
      await loadPainPoints();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return painPoints;
    return painPoints.filter(
      (p) =>
        String(p.number).includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.frustration.toLowerCase().includes(q) ||
        p.feeling.toLowerCase().includes(q),
    );
  }, [painPoints, search]);

  const logout = () => {
    authApi.logout();
    navigate("/admin");
  };

  return (
    <div className="min-h-[100dvh]">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 glass border-b border-white/40">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <Logo className="h-7" />
            <span className="hidden sm:inline text-xs font-bold text-slate-500 uppercase tracking-wider">
              Admin
            </span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/admin/trainings")}
            data-testid="admin-trainings-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white/80 hover:bg-white text-[#0F172A] px-3 py-1.5 text-sm font-semibold transition-all"
          >
            Trainings
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/onboarding-programs")}
            data-testid="admin-programs-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white/80 hover:bg-white text-[#0F172A] px-3 py-1.5 text-sm font-semibold transition-all"
          >
            Programs
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/channel-partners")}
            data-testid="admin-cp-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white/80 hover:bg-white text-[#0F172A] px-3 py-1.5 text-sm font-semibold transition-all"
          >
            Channel Partners
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/team-users")}
            data-testid="admin-team-users-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white/80 hover:bg-white text-[#0F172A] px-3 py-1.5 text-sm font-semibold transition-all"
          >
            Team Users
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/progress")}
            data-testid="admin-progress-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white/80 hover:bg-white text-[#0F172A] px-3 py-1.5 text-sm font-semibold transition-all"
          >
            Progress
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/templates")}
            data-testid="admin-templates-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white/80 hover:bg-white text-[#0F172A] px-3 py-1.5 text-sm font-semibold transition-all"
          >
            Templates
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/api-docs")}
            data-testid="admin-api-docs-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white/80 hover:bg-white text-[#0F172A] px-3 py-1.5 text-sm font-semibold transition-all"
          >
            API
          </button>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white/80 hover:bg-white text-[#0F172A] px-3 py-1.5 text-sm font-semibold transition-all"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 pt-24 pb-12">
        {/* Tabs */}
        <div className="inline-flex bg-white/70 backdrop-blur-md rounded-full p-1 border border-white/80 mb-6">
          <TabButton active={tab === "pain-points"} onClick={() => setTab("pain-points")} testId="tab-pain-points">
            Pain Points
          </TabButton>
          <TabButton active={tab === "analytics"} onClick={() => setTab("analytics")} testId="tab-analytics">
            <BarChart3 className="w-4 h-4 inline mr-1" /> Analytics
          </TabButton>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5">
            {error}
          </div>
        )}

        {tab === "pain-points" && (
          <>
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="pp-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by number, category, text…"
                  className="w-full pl-9 pr-4 py-2.5 rounded-full glass-input focus:outline-none focus:border-[#FF5E1A] text-sm text-[#0F172A]"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEditing({ isNew: true })}
                  data-testid="pp-add-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-4 py-2 text-sm font-semibold transition-all"
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
                <button
                  type="button"
                  onClick={handleReseed}
                  disabled={busy}
                  data-testid="pp-reseed-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/70 hover:bg-white border border-white/80 text-[#0F172A] px-4 py-2 text-sm font-semibold transition-all disabled:opacity-50"
                >
                  <RefreshCw className="w-4 h-4" /> Reseed
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-16 text-slate-500">Loading…</div>
            ) : (
              <div className="glass-strong rounded-3xl overflow-hidden">
                <div className="max-h-[70vh] overflow-y-auto">
                  <table className="w-full text-sm" data-testid="pp-table">
                    <thead className="bg-white/60 sticky top-0 backdrop-blur-md z-10">
                      <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                        <th className="px-4 py-3 w-14">#</th>
                        <th className="px-4 py-3 w-40">Category</th>
                        <th className="px-4 py-3">Frustration</th>
                        <th className="px-4 py-3 hidden lg:table-cell">Solution</th>
                        <th className="px-4 py-3 w-24">Active</th>
                        <th className="px-4 py-3 w-24 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p) => (
                        <tr
                          key={p.id}
                          className="border-t border-white/60 hover:bg-white/40 transition-colors"
                          data-testid={`pp-row-${p.number}`}
                        >
                          <td className="px-4 py-3 font-semibold text-slate-700">
                            {p.number}
                          </td>
                          <td className="px-4 py-3 text-xs font-semibold text-[#2A5CB8]">
                            {p.category}
                          </td>
                          <td className="px-4 py-3 text-[#0F172A] font-medium">
                            <div>{p.frustration}</div>
                            <div className="text-xs italic text-slate-500 mt-0.5">
                              {p.feeling}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[#FF5E1A] font-medium hidden lg:table-cell">
                            {p.solution || <span className="text-slate-400 italic font-normal">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                p.active
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-200 text-slate-500"
                              }`}
                            >
                              {p.active ? "Live" : "Off"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex gap-1">
                              <button
                                type="button"
                                onClick={() => setEditing(p)}
                                data-testid={`pp-edit-${p.number}`}
                                className="p-2 rounded-lg text-slate-500 hover:text-[#FF5E1A] hover:bg-white transition-colors"
                                aria-label="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(p.id)}
                                data-testid={`pp-delete-${p.number}`}
                                className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-white transition-colors"
                                aria-label="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                            No pain points match your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {tab === "analytics" && (
          <AnalyticsPanel analytics={analytics} onReload={loadAnalytics} />
        )}
      </div>

      {editing && (
        <EditModal
          initial={editing.isNew ? null : editing}
          onSave={handleSave}
          onClose={() => setEditing(null)}
          busy={busy}
        />
      )}
    </div>
  );
}

const TabButton = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
      active
        ? "bg-white text-[#0F172A] shadow-sm"
        : "text-[#64748B] hover:text-[#0F172A]"
    }`}
  >
    {children}
  </button>
);

const EditModal = ({ initial, onSave, onClose, busy }) => {
  const [form, setForm] = useState(
    initial || {
      number: 0,
      category: "",
      frustration: "",
      feeling: "",
      solution: "",
      active: true,
    },
  );

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      number: Number(form.number) || 0,
      category: form.category.trim(),
      frustration: form.frustration.trim(),
      feeling: form.feeling.trim(),
      solution: (form.solution || "").trim(),
      active: !!form.active,
    };
    if (!payload.category || !payload.frustration) return;
    onSave(payload, initial?.id || null);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg glass-strong rounded-3xl p-6 md:p-7 flex flex-col gap-4"
        data-testid="pp-edit-modal"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl font-bold text-[#0F172A]">
            {initial ? "Edit pain point" : "New pain point"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full text-slate-500 hover:bg-white/70"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="col-span-1 flex flex-col gap-1">
            <span className="text-xs font-semibold text-slate-600">Number</span>
            <input
              type="number"
              value={form.number}
              onChange={(e) => setForm({ ...form, number: e.target.value })}
              data-testid="pp-form-number"
              className="px-3 py-2 rounded-xl glass-input text-sm text-[#0F172A]"
            />
          </label>
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-xs font-semibold text-slate-600">Category</span>
            <input
              type="text"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="pp-form-category"
              className="px-3 py-2 rounded-xl glass-input text-sm text-[#0F172A]"
              required
            />
          </label>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-slate-600">
            Frustration (headline)
          </span>
          <textarea
            value={form.frustration}
            onChange={(e) => setForm({ ...form, frustration: e.target.value })}
            data-testid="pp-form-frustration"
            className="px-3 py-2 rounded-xl glass-input text-sm text-[#0F172A] min-h-[70px]"
            required
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-slate-600">
            What the CP actually feels
          </span>
          <textarea
            value={form.feeling}
            onChange={(e) => setForm({ ...form, feeling: e.target.value })}
            data-testid="pp-form-feeling"
            className="px-3 py-2 rounded-xl glass-input text-sm text-[#0F172A] min-h-[70px]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-slate-600">
            ChannelPartner.Network solution
          </span>
          <textarea
            value={form.solution || ""}
            onChange={(e) => setForm({ ...form, solution: e.target.value })}
            data-testid="pp-form-solution"
            className="px-3 py-2 rounded-xl glass-input text-sm text-[#0F172A] min-h-[80px]"
            placeholder="How ChannelPartner.Network solves this pain point"
          />
        </label>
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input
            type="checkbox"
            checked={!!form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            data-testid="pp-form-active"
            className="w-4 h-4 accent-[#FF5E1A]"
          />
          Active (show on public swipe deck)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:bg-white/70"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="pp-form-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5E1A] hover:bg-[#E04F00] disabled:opacity-60 text-white px-5 py-2 text-sm font-semibold transition-all"
          >
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </form>
    </div>
  );
};

const AnalyticsPanel = ({ analytics, onReload }) => {
  if (!analytics) {
    return <div className="text-slate-500 py-8 text-center">Loading analytics…</div>;
  }
  const { totals, pain_points } = analytics;
  return (
    <div className="flex flex-col gap-5" data-testid="analytics-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Sessions" value={totals.sessions} />
        <StatCard label="Completed" value={totals.completed} />
        <StatCard label="Joined" value={totals.joined} accent />
        <StatCard
          label="Join rate"
          value={`${Math.round((totals.join_rate || 0) * 100)}%`}
          accent
        />
      </div>
      <div className="glass-strong rounded-3xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/60">
          <h3 className="text-sm font-bold text-[#0F172A]">
            Top swiped pain points
          </h3>
          <button
            type="button"
            onClick={onReload}
            className="text-xs font-semibold text-[#FF5E1A] hover:underline"
          >
            Refresh
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/60 sticky top-0 backdrop-blur-md">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-4 py-2 w-14">#</th>
                <th className="px-4 py-2 w-36">Category</th>
                <th className="px-4 py-2">Frustration</th>
                <th className="px-4 py-2 w-20 text-right">Likes</th>
                <th className="px-4 py-2 w-20 text-right">Skips</th>
                <th className="px-4 py-2 w-24 text-right">Like rate</th>
              </tr>
            </thead>
            <tbody>
              {pain_points.map((r) => (
                <tr key={r.id} className="border-t border-white/60">
                  <td className="px-4 py-2 font-semibold text-slate-700">
                    {r.number}
                  </td>
                  <td className="px-4 py-2 text-[#2A5CB8] text-xs font-semibold">
                    {r.category}
                  </td>
                  <td className="px-4 py-2 text-[#0F172A]">{r.frustration}</td>
                  <td className="px-4 py-2 text-right text-[#FF5E1A] font-bold">
                    <Heart className="w-3 h-3 inline mr-1 fill-current" />
                    {r.likes}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-500 font-semibold">
                    <CloseIcon className="w-3 h-3 inline mr-1" />
                    {r.skips}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold">
                    {Math.round((r.like_rate || 0) * 100)}%
                  </td>
                </tr>
              ))}
              {pain_points.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    No swipes recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div
    className={`rounded-2xl p-4 ${
      accent
        ? "bg-[#FFF3ED] border border-[#FFD9C2]"
        : "glass"
    }`}
  >
    <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
      {label}
    </p>
    <p
      className={`text-2xl md:text-3xl font-heading font-bold mt-1 ${
        accent ? "text-[#FF5E1A]" : "text-[#0F172A]"
      }`}
    >
      {value}
    </p>
  </div>
);
